import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useMyTrainingsDatatable } from 'generated/resources/datatable.graphql'
import { TrainingsSessionModalityEnum } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { AttendanceTag } from 'modules/trainings/components/MyTrainings/MyTrainingsDatatable/AttendanceTag'
import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'
import { formatSessionDate } from 'modules/trainings/lib/utils'

import { ModalityFilter } from './Filters/ModalityFilter'
import { statusFilter } from './Filters/StatusFilter'
import { yearFilter } from './Filters/YearFilter'

export const CoursesDatatable = () => {
  const { navigation } = useNavigation()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { access } = useCurrentSession()
  const { getModalityTranslation, getModalityValue, getModality } = useTrainingsDatatable()

  const getIcon = (modality: TrainingsSessionModalityEnum) => {
    switch (modality) {
      case 'inperson':
        return <Icon.Medium icon="pin" />
      case 'mixed':
        return <Icon.Medium icon="user" />
      case 'online':
        return <Icon.Medium icon="laptop" />
      default:
        return null
    }
  }

  const source = useMyTrainingsDatatable(
    [
      {
        identifier: 'name',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.name_column'),
        render: ({ name }) => name,
        template: '{{ name }}',
      },
      {
        identifier: 'nextSession',
        kind: ProjectionKind.Custom,
        title: i18n.t(
          'trainings.my_trainings_revamp.overview.courses_datatable.next_session_column'
        ),
        render: ({ sessions }) => {
          const nextSessionDate = sessions[0]
          if (!nextSessionDate)
            return i18n.t('trainings.my_trainings_revamp.overview.no_more_sessions')
          const date = formatSessionDate({
            startsAt: nextSessionDate.startsAt,
            endsAt: nextSessionDate.endsAt,
            duration: nextSessionDate.duration ?? '',
            dueDate: nextSessionDate.dueDate,
            schedule: nextSessionDate.schedule,
          })
          return date
        },
        template: '{{ nextSession }}',
      },
      {
        identifier: 'modality',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.modality_column'),
        render: ({ sessions }) => {
          const sessionModalities = sessions
            .map((session) => session.modality)
            .filter(Boolean)
            .map((modality) => getModality(modality))
          const modality = getModalityValue(sessionModalities)
          const translation = getModalityTranslation(modality)
          if (modality && translation)
            return (
              <Box flexDirection="row" gap="s4">
                {getIcon(modality)}
                <Text size="300">{translation}</Text>
              </Box>
            )
        },
        template: '{{modality}}',
      },
      {
        identifier: 'status',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.status_column'),
        render: ({ id }) => {
          return <AttendanceTag accessId={access.id} trainingId={id} />
        },
        template: '{{ status }}',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: ({ id }) => {
          return (
            <Box alignItems="flexEnd">
              <Button.Router
                type="secondary"
                to={navigation.myTrainings.revamp.detailsRevamp.view.overview.fullPath({
                  trainingId: id,
                })}
                iconName="arrow-right"
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        search: debouncedSearch,
        accessId: access.id,
        status: 'active',
        onlyAssigned: true,
        next: true,
      },
    }
  )
  return (
    <DataTable
      filters={[ModalityFilter(source), yearFilter(source), statusFilter(source)]}
      title={i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.title')}
      source={source}
      disableDefaultActions
      search={{
        placeholder: i18n.t('common.search'),
        value: search,
        onChange: onSearch,
      }}
    />
  )
}
